import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { PlayArrow as PlayIcon, Visibility as PreviewIcon } from '@material-ui/icons';

import type { AnalyticsScheduleView, ReportType } from '../types';
import { EnabledChip, LastExecutionStatus } from './StatusChips';

const useStyles = makeStyles(theme => ({
  paper: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  tableWrapper: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  orgName: {
    fontWeight: 500,
  },
  orgIdCell: {
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    width: 60,
  },
  orgCell: {
    minWidth: 200,
  },
  reportCell: {
    minWidth: 160,
    padding: theme.spacing(1.5),
  },
  reportCellContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  reportCellHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  recipientText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  recipientClickable: {
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  scheduleTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  timezoneCell: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
  actionsCell: {
    width: 120,
    textAlign: 'right' as const,
  },
  actionButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
  },
}));

interface SchedulesTableProps {
  schedules: AnalyticsScheduleView[];
  loading: boolean;
  lambdaEnabled: boolean;
  searchQuery: string;
  onPreview: (orgId: number, orgName: string, reportType: ReportType) => void;
  onTrigger: (orgId: number, orgName: string, reportType: ReportType, recipientCount: number) => void;
  onViewRecipients: (orgId: number, orgName: string, reportType: ReportType) => void;
}

export const SchedulesTable: React.FC<SchedulesTableProps> = ({
  schedules,
  loading,
  lambdaEnabled,
  searchQuery,
  onPreview,
  onTrigger,
  onViewRecipients,
}) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (schedules.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Box className={classes.emptyState}>
          <Typography variant="body1" color="textSecondary">
            {searchQuery
              ? `No organizations match "${searchQuery}"`
              : 'No schedules found. Click "Sync" to create default schedules for all organizations.'}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <TableContainer className={classes.tableWrapper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 60 }}>ID</TableCell>
              <TableCell style={{ minWidth: 200 }}>Organization</TableCell>
              <TableCell style={{ minWidth: 170 }}>Weekly</TableCell>
              <TableCell style={{ minWidth: 170 }}>Monthly</TableCell>
              <TableCell style={{ minWidth: 170 }}>Daily CSV</TableCell>
              <TableCell style={{ minWidth: 140 }}>Timezone</TableCell>
              {lambdaEnabled && (
                <TableCell style={{ width: 120 }} align="right">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map(schedule => (
              <ScheduleRow
                key={schedule.orgId}
                schedule={schedule}
                lambdaEnabled={lambdaEnabled}
                onPreview={onPreview}
                onTrigger={onTrigger}
                onViewRecipients={onViewRecipients}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

interface ScheduleRowProps {
  schedule: AnalyticsScheduleView;
  lambdaEnabled: boolean;
  onPreview: (orgId: number, orgName: string, reportType: ReportType) => void;
  onTrigger: (orgId: number, orgName: string, reportType: ReportType, recipientCount: number) => void;
  onViewRecipients: (orgId: number, orgName: string, reportType: ReportType) => void;
}

const ScheduleRow: React.FC<ScheduleRowProps> = ({
  schedule,
  lambdaEnabled,
  onPreview,
  onTrigger,
  onViewRecipients,
}) => {
  const classes = useStyles();

  return (
    <TableRow hover>
      <TableCell className={classes.orgIdCell}>{schedule.orgId}</TableCell>
      <TableCell className={classes.orgCell}>
        <Typography className={classes.orgName}>{schedule.orgName}</Typography>
        {schedule.orgSlug && (
          <Typography variant="caption" color="textSecondary" display="block">
            {schedule.orgSlug}
          </Typography>
        )}
      </TableCell>

      {/* Weekly Report Cell */}
      <ReportCell
        enabled={schedule.weeklyEnabled}
        recipientCount={schedule.weeklyRecipientCount}
        scheduleTime="Mon 8:00 AM"
        lastExecution={schedule.weeklyLastExecution}
        reportType="weekly"
        orgId={schedule.orgId}
        orgName={schedule.orgName}
        lambdaEnabled={lambdaEnabled}
        onPreview={onPreview}
        onViewRecipients={onViewRecipients}
      />

      {/* Monthly Report Cell */}
      <ReportCell
        enabled={schedule.monthlyEnabled}
        recipientCount={schedule.monthlyRecipientCount}
        scheduleTime="1st of month 8:00 AM"
        lastExecution={schedule.monthlyLastExecution}
        reportType="monthly"
        orgId={schedule.orgId}
        orgName={schedule.orgName}
        lambdaEnabled={lambdaEnabled}
        onPreview={onPreview}
        onViewRecipients={onViewRecipients}
      />

      {/* Daily CSV Cell */}
      <TableCell className={classes.reportCell}>
        <Box className={classes.reportCellContent}>
          {schedule.dailyCsvBucket ? (
            <>
              <Box className={classes.reportCellHeader}>
                <EnabledChip enabled={schedule.dailyCsvEnabled} />
                <Tooltip title={`S3 Bucket: ${schedule.dailyCsvBucket}`}>
                  <Typography
                    className={classes.recipientText}
                    style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {schedule.dailyCsvBucket}
                  </Typography>
                </Tooltip>
                {lambdaEnabled && (
                  <Tooltip title="Preview daily CSV">
                    <IconButton
                      size="small"
                      onClick={() => onPreview(schedule.orgId, schedule.orgName, 'daily_csv')}
                      className={classes.actionButton}
                    >
                      <PreviewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Typography className={classes.scheduleTime}>Daily 12:01 AM</Typography>
              <LastExecutionStatus execution={schedule.dailyCsvLastExecution} />
            </>
          ) : (
            <Chip label="Not configured" size="small" variant="outlined" />
          )}
        </Box>
      </TableCell>

      <TableCell className={classes.timezoneCell}>{schedule.timezone}</TableCell>

      {/* Actions Cell - only show when Lambda is enabled */}
      {lambdaEnabled && (
        <TableCell className={classes.actionsCell}>
          <Box display="flex" justifyContent="flex-end" style={{ gap: 4, flexWrap: 'wrap' }}>
            <TriggerButton
              reportType="weekly"
              enabled={schedule.weeklyEnabled}
              lambdaEnabled={lambdaEnabled}
              onClick={() => onTrigger(schedule.orgId, schedule.orgName, 'weekly', schedule.weeklyRecipientCount)}
              color="primary"
              tooltip="Trigger weekly (sends emails!)"
            />
            <TriggerButton
              reportType="monthly"
              enabled={schedule.monthlyEnabled}
              lambdaEnabled={lambdaEnabled}
              onClick={() => onTrigger(schedule.orgId, schedule.orgName, 'monthly', schedule.monthlyRecipientCount)}
              color="secondary"
              tooltip="Trigger monthly (sends emails!)"
            />
            <TriggerButton
              reportType="daily_csv"
              enabled={schedule.dailyCsvEnabled && !!schedule.dailyCsvBucket}
              lambdaEnabled={lambdaEnabled}
              onClick={() => onTrigger(schedule.orgId, schedule.orgName, 'daily_csv', 0)}
              color="action"
              tooltip={!schedule.dailyCsvBucket ? 'No S3 bucket configured' : 'Trigger daily CSV (uploads to S3)'}
            />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

interface ReportCellProps {
  enabled: boolean;
  recipientCount: number;
  scheduleTime: string;
  lastExecution?: AnalyticsScheduleView['weeklyLastExecution'];
  reportType: ReportType;
  orgId: number;
  orgName: string;
  lambdaEnabled: boolean;
  onPreview: (orgId: number, orgName: string, reportType: ReportType) => void;
  onViewRecipients: (orgId: number, orgName: string, reportType: ReportType) => void;
}

const ReportCell: React.FC<ReportCellProps> = ({
  enabled,
  recipientCount,
  scheduleTime,
  lastExecution,
  reportType,
  orgId,
  orgName,
  lambdaEnabled,
  onPreview,
  onViewRecipients,
}) => {
  const classes = useStyles();
  const hasRecipients = recipientCount > 0;

  return (
    <TableCell className={classes.reportCell}>
      <Box className={classes.reportCellContent}>
        <Box className={classes.reportCellHeader}>
          <EnabledChip enabled={enabled} />
          <Tooltip title={hasRecipients ? 'Click to view recipients' : 'No recipients configured'}>
            <Typography
              className={hasRecipients ? classes.recipientClickable : classes.recipientText}
              onClick={hasRecipients ? () => onViewRecipients(orgId, orgName, reportType) : undefined}
            >
              {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
            </Typography>
          </Tooltip>
          {lambdaEnabled && (
            <Tooltip title={`Preview ${reportType}`}>
              <IconButton
                size="small"
                onClick={() => onPreview(orgId, orgName, reportType)}
                className={classes.actionButton}
              >
                <PreviewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography className={classes.scheduleTime}>{scheduleTime}</Typography>
        <LastExecutionStatus execution={lastExecution} />
      </Box>
    </TableCell>
  );
};

interface TriggerButtonProps {
  reportType: string;
  enabled: boolean;
  lambdaEnabled: boolean;
  onClick: () => void;
  color: 'primary' | 'secondary' | 'action';
  tooltip: string;
}

const TriggerButton: React.FC<TriggerButtonProps> = ({ enabled, lambdaEnabled, onClick, color, tooltip }) => {
  const classes = useStyles();
  const isDisabled = !lambdaEnabled || !enabled;
  const fullTooltip = !lambdaEnabled ? 'Trigger requires Lambda' : tooltip;

  return (
    <Tooltip title={fullTooltip}>
      <span>
        <IconButton size="small" onClick={onClick} className={classes.actionButton} disabled={isDisabled}>
          <PlayIcon fontSize="small" color={isDisabled ? 'disabled' : color} />
        </IconButton>
      </span>
    </Tooltip>
  );
};
